import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";


export default function CriticalBulletinBanner() {
  const { user } = useAuth();
  const { activeOrganizationId, canUseOrganizationScope, scopeVersion } = useOrganizationScope();
  const [criticalBulletins, setCriticalBulletins] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCritical = async () => {
      // Check if user is authenticated
      if (!user || !canUseOrganizationScope) {
        setLoading(false);
        return;
      }

      try {
        // Uses httpOnly cookies via withCredentials
        const res = await api.get(`/bulletins/unread-critical`, { signal: controller.signal });
        setCriticalBulletins(res.data || []);
      } catch (err) {
        if (err.code !== "ERR_CANCELED") {
          console.error("Failed to fetch critical bulletins:", err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    setCriticalBulletins([]);
    setCurrentIndex(0);
    setShowDetail(false);
    setDismissed(false);
    setLoading(Boolean(user && canUseOrganizationScope));

    if (!user || !canUseOrganizationScope) {
      return () => controller.abort();
    }

    // Fetch when user is authenticated
    fetchCritical();
    
    // Poll every 5 minutes
    const interval = setInterval(() => {
      if (user) {
        fetchCritical();
      }
    }, 5 * 60 * 1000);
    
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [activeOrganizationId, canUseOrganizationScope, scopeVersion, user]);

  const handleConfirmRead = async () => {
    const bulletin = criticalBulletins[currentIndex];
    if (!bulletin) return;

    try {
      setConfirming(true);
      // Uses httpOnly cookies via withCredentials
      await api.post(`/bulletins/${bulletin.id}/read?confirmed=true`);

      // Remove from list
      const newList = criticalBulletins.filter((b) => b.id !== bulletin.id);
      setCriticalBulletins(newList);
      setShowDetail(false);
      
      // Dispatch event to notify the notification badge to refresh
      window.dispatchEvent(new CustomEvent('bulletinRead'));
      
      if (newList.length === 0) {
        setDismissed(true);
      } else if (currentIndex >= newList.length) {
        setCurrentIndex(newList.length - 1);
      }
    } catch (err) {
      console.error("Failed to confirm read:", err);
    } finally {
      setConfirming(false);
    }
  };

  // Don't show if loading, no critical bulletins, or dismissed
  if (loading || dismissed || criticalBulletins.length === 0) {
    return null;
  }

  const currentBulletin = criticalBulletins[currentIndex];

  return (
    <>
      {/* Banner */}
      <div
        className="bg-red-600 text-white px-4 py-3 flex items-center justify-between gap-4"
        data-testid="critical-bulletin-banner"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-1.5 bg-red-500 rounded-lg">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm uppercase tracking-wide">
                Critical Bulletin
              </span>
              {criticalBulletins.length > 1 && (
                <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full">
                  {currentIndex + 1} of {criticalBulletins.length}
                </span>
              )}
            </div>
            <p className="text-sm truncate opacity-90">
              {currentBulletin?.title}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDetail(true)}
            className="bg-white text-red-600 hover:bg-red-50"
            data-testid="read-critical-btn"
          >
            Read & Confirm
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          
          {criticalBulletins.length > 1 && (
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                disabled={currentIndex === 0}
                className="p-1 rounded hover:bg-red-500 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={() => setCurrentIndex((i) => Math.min(criticalBulletins.length - 1, i + 1))}
                disabled={currentIndex === criticalBulletins.length - 1}
                className="p-1 rounded hover:bg-red-500 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal with custom high-z-index overlay */}
      {showDetail && (
        <div 
          className="fixed inset-0 bg-black/90"
          style={{ zIndex: 9998 }}
          onClick={() => {}} // Prevent clicks from passing through
        />
      )}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          style={{ zIndex: 9999 }}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase">Critical Bulletin</span>
            </div>
            <DialogTitle className="text-xl">
              {currentBulletin?.title}
            </DialogTitle>
            <DialogDescription>
              Effective: {currentBulletin && new Date(currentBulletin.effective_date).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          <div className="prose prose-sm dark:prose-invert max-w-none py-4">
            <ReactMarkdown>{currentBulletin?.content || ""}</ReactMarkdown>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-300">
              You must confirm that you have read and understood this critical bulletin before you can dismiss it.
            </p>
          </div>

          <DialogFooter>
            <Button
              className="w-full bg-red-600 hover:bg-red-700"
              onClick={handleConfirmRead}
              disabled={confirming}
              data-testid="confirm-critical-btn"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              I have read and understood this bulletin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
